import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, NavLink, Row } from 'reactstrap';
import { useAPIErrorContext } from '../../../contexts/apiErrorContext'
import { useFetchWrapper } from '../../../hooks/useFetchWrapper';
import Helper from '../../../utilities/helper';
import { FineTuneControl } from './FineTuneControl';
import { FundingSources } from './FundingSources';
import { GreenHouseGas } from './GreenHouseGas';
import { RecommendedEquipment } from './RecommendedEquipment';
import { SustainedImpact } from './SustainedImpact';
import { TopLevelResults } from './TopLevelResults';
import { PermissionsGate } from '../../../utilities/PermissionsGate';
import { ACCESS_VALUES, PageNames } from '../../../utilities/constValues';
import { ErrorBanner } from '../../Shared/ErrorBanner';
import { clientConfig } from '../../../utilities/configService';
import { AnchorLink, PageAnchors } from '../../Shared/PageAnchors';
import { ContentBlock } from '../../Shared/Content/ContentBlock';
import { ContentHeading } from '../../Shared/Content/ContentHeading';
import { ContentBody } from '../../Shared/Content/ContentBody';
import { WhatsNext } from './WhatsNextSection/WhatsNext';

interface ITopLevelProps {
    fleetID: string,
    tco: number,
    annualFuelSavings: number,
    annualOandMSavings: number,
    cO2Reductions: number
}

interface IRealWorldEquivalents {
    homeEnergyUse: string,
    incandescentSwap: string,
    wasteToLandfill: string,
    treesPlanted: string
}

export interface IVehicleRecommendations {
    vehicleCategoryName: string,
    replacementVehicleType: string,
    replacementVehicleCount: number,
    avgVehicleRange: number,
    avgBatteryCapacity: number,
    imagePath: string
    imageID: number
}

export interface IChargerRecommendations {
    replacementChargerType: string,
    replacementChargerCount: string,
    chargerTypeNameLong: string,
    description: string,
    chargeLoadRange: string,
    chargeTimeRange: string,
    input: string,
    imagePath: string
}

interface IAnnualEmissions {
    currentFleetEstimatedAnnualEmissions: number,
    replacementFleetEstimatedAnnualEmissions: number
}

interface IFleetSummary {
    fleetName: string,
    fleetID: string,
    isSample: boolean,
    isDefault: boolean,
    totalVehicleCount: number,
    vehicleTypes: IVehicleSet[]
}

interface IVehicleSet {
    vehicleTypeName: string,
    vehicleCount: number,
    imagePath: string
}

export const ViewEstimate = () => {
    const fetchWrapper = useFetchWrapper();
    const { addError, clearError } = useAPIErrorContext();
    const applicationName = clientConfig?.APPLICATION_NAME || '';
    const navigate = useNavigate();
    const location = useLocation();
    const [showFineTuning, setShowFineTuning] = useState(false);
    const [fleetID, setFleetID] = useState('');
    const [fleetSummary, setFleetSummary] = useState<IFleetSummary>();
    const [updateEstimate, setUpdateEstimate] = useState(false);
    const [topLevelResults, setTopLevelResults] = useState<ITopLevelProps>();
    const [realWorldEquivResults, setRealWorldEquivResults] = useState<IRealWorldEquivalents>();
    const [annualEmissionsResults, setAnnualEmissionsResults] = useState<IAnnualEmissions>();
    const [vehicleRecommendations, setVehicleRecommendations] = useState<IVehicleRecommendations[]>();
    const [chargerRecommendations, setChargerRecommendations] = useState<IChargerRecommendations[]>();
    const pageAnchors: AnchorLink[] = [{ displayName: "Principal Results", hashLocation: "#PrincipalResults" },
    { displayName: "Sustained Impact", hashLocation: "#SustainedImpact" },
    { displayName: "Funding", hashLocation: "#Funding" }];


    React.useEffect(() => {
        Helper.setPagePath(location.pathname);
    }, [location]);

    const { fleetIDParam } = useParams();
    if (!fleetID) {
        if (fleetIDParam) {
            setFleetID(fleetIDParam);
        }
    };

    useEffect(() => {
        getFleetByID(fleetID);
        getTopLevelResults();
        getRealWorldEquivalents();
        getAnnualEmissions();
        getVehicleRecommendations();
        getChargerRecommendations();
    }, [fleetID]);

    useEffect(() => {
        if (updateEstimate) {
            getFleetByID(fleetID);
            getTopLevelResults();
            getRealWorldEquivalents();
            getAnnualEmissions();
            getVehicleRecommendations();
            getChargerRecommendations();
        }
    }, [updateEstimate]);

    const showHideFineTuning = (e: any) => {
        e.preventDefault();

        if (showFineTuning) {
            setShowFineTuning(false);
        }
        else {
            setShowFineTuning(true);
        }
    };

    const duplicateFleet = () => {
        clearError();
        var duplicateFleetRequest = {
            FleetID: fleetID,
            FleetName: fleetSummary?.fleetName ? "Copy of " + fleetSummary?.fleetName : "Copy of Sample Fleet",
            ApplicationName: applicationName
        };

        fetchWrapper.post('fleet/duplicate', 'application/json', duplicateFleetRequest)
            .then((data) => {
                if (data.success) {
                    navigate("/ViewFleets");
                } else {
                    addError("Error submitting form", data.message);
                }
            }).catch((error: any) => {
                addError("Error submitting form", error);
            });
    }

    const updateFleetEstimate = () => {
        setUpdateEstimate(true);
    };

    const getFleetByID = (summaryFleetID: any) => {
        fetchWrapper.get(`fleet/fleetRollup/${summaryFleetID}/2`)
            .then((data) => {
                if (data.fleetRollup) {
                    setFleetSummary(data.fleetRollup);
                }
            });
    };

    const getTopLevelResults = () => {
        fetchWrapper.get(`fleetestimator/toplevelcalcs/${applicationName}/${fleetID}`)
            .then((data) => {
                if (data.topLevelCalcs) {
                    setTopLevelResults(data.topLevelCalcs);
                }
                setUpdateEstimate(false);
            });
    };

    const getRealWorldEquivalents = () => {
        fetchWrapper.get(`fleetestimator/realworldequivalents/${fleetID}/0`)
            .then((data) => {
                if (data.realWorldEquivalents) {
                    setRealWorldEquivResults(data.realWorldEquivalents);
                }
            });
    };

    const getAnnualEmissions = () => {
        fetchWrapper.get(`fleetestimator/annualemission/${fleetID}/0`)
            .then((data) => {
                if (data.annualEmission) {
                    setAnnualEmissionsResults(data.annualEmission);
                }
            });
    };

    const getVehicleRecommendations = () => {
        fetchWrapper.get(`fleetestimator/vehiclerecommendation/${fleetID}/0`)
            .then((data) => {
                if (data.vehicleRecommendations) {
                    setVehicleRecommendations(data.vehicleRecommendations);
                }
            });
    };

    const getChargerRecommendations = () => {
        fetchWrapper.get(`fleetestimator/chargerrecommendations/${fleetID}/0`)
            .then((data) => {
                if (data.chargerRecommendations) {
                    setChargerRecommendations(data.chargerRecommendations);
                }
            });
    };



    return (
        <div className="viewEstimate">
            <Row className="contentPageHeading">
                <Col className="titleBlock">
                    <Row>
                        <Col sm={8}>
                            <h1 className="title css_heading bold">Results for {fleetSummary?.fleetName}</h1>
                        </Col>
                        <Col sm={4} className="text-align-right desktopView hidePrintButton">
                            <Button className="submitButton hollowNeg tertiaryButton iconRightArrow" onClick={() => window.open(`/PrintResults/${fleetID}`, '_blank')}>Print Results</Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <ErrorBanner key="createEditFleet" errorCode="form" />
            <PageAnchors anchorLinks={pageAnchors}>
                <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<Col sm={4} className="nextPageNav"></Col>} pageName={PageNames.ViewEstimate}>
                    <Col sm={4} className="nextPageNav">
                        {fleetSummary?.isSample ?
                            (
                                < NavLink href="#" className="iconPlusSignBlue" onClick={duplicateFleet}>Save to My Fleets</NavLink >
                            ) : (
                                <Button className={`buttonAsLink ${showFineTuning ? 'active' : ''} openCloseChevron `} onClick={showHideFineTuning}>Fine Tune These Results</Button>
                            )}
                    </Col>
                </PermissionsGate>
            </PageAnchors>
            <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<></>} pageName={PageNames.ViewEstimate}>
                {showFineTuning ? (
                    <FineTuneControl fleetID={fleetID} applicationName={applicationName} closeHandler={showHideFineTuning} submitHandler={updateFleetEstimate} />
                ) : (<></>)}
            </PermissionsGate>
            <ContentBlock className="viewEstimatePrincipalResults">
                <ContentBody>
                    <a id="PrincipalResults" className={'visually-hidden'}></a>
                    <ContentHeading title="Principal Results" />
                    <Row className="sectionPadding">
                        <Col>
                            {topLevelResults ? (
                                <TopLevelResults topLevelData={topLevelResults} />
                            ) : ''}
                        </Col>
                    </Row>
                </ContentBody>
            </ContentBlock>
            <ContentBlock className="viewEstimateGreenHouse">
                <ContentBody>
                    <Row className="sectionPadding">
                        <Col>
                            {realWorldEquivResults && annualEmissionsResults ? (
                                <GreenHouseGas realWorldData={realWorldEquivResults} emissionsData={annualEmissionsResults} isPrintView={false} />
                            ) : ''}
                        </Col>
                    </Row>
                </ContentBody>
            </ContentBlock>
            <ContentBlock className="viewEstimateReqEquipment">
                <ContentBody>
                    <Row>
                        <Col className="recommendsSection">
                            {chargerRecommendations && vehicleRecommendations ? (
                                <RecommendedEquipment chargerData={chargerRecommendations} vehicleData={vehicleRecommendations} isPrintView={false} />
                            ) : ''}
                        </Col>
                    </Row>
                </ContentBody>
            </ContentBlock>
            <ContentBlock className="viewEstimateSustainedImpact">
                <ContentBody>
                    <a id="SustainedImpact" className={'visually-hidden'}></a>
                    <ContentHeading title="Sustained Impact"/>
                    <Row className="sectionPadding">
                        <Col>
                            <SustainedImpact fleetID={fleetID}/>
                        </Col>
                    </Row>
                </ContentBody>
            </ContentBlock>
            <a id="Funding" className={'visually-hidden'}></a>
            <FundingSources fleetID={fleetID} />
            <a id="WhatsNext" className={'visually-hidden'}></a>
            <WhatsNext isSampleFleet={(fleetSummary && fleetSummary.isSample) ? fleetSummary.isSample : false} fleetID={fleetID} />
        </div>
    );
}
